import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, apiErr } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PublicQuestionnaire() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { axios.get(`${API}/public/questionnaire/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(apiErr(e))); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await axios.post(`${API}/public/questionnaire/${token}`, { answers }); setDone(true); window.scrollTo(0, 0); }
    catch (e2) { setErr(apiErr(e2)); } finally { setBusy(false); }
  };
  const set = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const finished = done || info?.status === "Submitted";

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-xl mx-auto">
        <div className="mb-6"><Brand /></div>
        {!info && !err && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {err && !info && <div data-testid="pq-error" className="rounded-2xl bg-white border border-slate-200 p-6 text-slate-700">{err}</div>}
        {info && finished && (
          <div data-testid="pq-done" className="rounded-2xl bg-white border border-slate-200 p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="font-head text-2xl font-extrabold text-slate-900">Thank you{info.client_first_name ? `, ${info.client_first_name}` : ""}!</h1>
            <p className="text-slate-500 mt-2">Your answers have been received by {info.company}. We'll be in touch soon.</p>
          </div>
        )}
        {info && !finished && (
          <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 space-y-5" data-testid="pq-form">
            <div>
              <h1 className="font-head text-2xl font-extrabold text-slate-900">A few quick questions</h1>
              <p className="text-slate-500 text-sm mt-1">Hi{info.client_first_name ? ` ${info.client_first_name}` : ""} — please answer these about your {info.service} file. It takes about 5 minutes.</p>
            </div>
            {info.questions.map((q, i) => (
              <div key={q.id}>
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">{i + 1}. {q.label}</label>
                <Field q={q} value={answers[q.id] || ""} onChange={(v) => set(q.id, v)} />
              </div>
            ))}
            {err && <div data-testid="pq-submit-error" className="text-sm rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2">{err}</div>}
            <button data-testid="pq-submit" type="submit" disabled={busy} className="w-full bg-primary text-white py-3 rounded-xl font-semibold disabled:opacity-60">{busy ? "Sending…" : "Submit answers"}</button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ q, value, onChange }) {
  const cls = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30";
  const tid = `pq-answer-${q.id}`;
  if (q.type === "textarea") return <textarea data-testid={tid} rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />;
  if (q.type === "date") return <input data-testid={tid} type="date" value={value} onChange={(e) => onChange(e.target.value)} className={cls} />;
  if (q.type === "yesno") return (
    <div className="flex gap-2">
      {["Yes", "No"].map((o) => (
        <button key={o} type="button" data-testid={`${tid}-${o.toLowerCase()}`} onClick={() => onChange(o)}
          className={`px-5 py-2 rounded-xl border text-sm font-semibold ${value === o ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600"}`}>{o}</button>
      ))}
    </div>
  );
  return <input data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />;
}
